/**
 * SEO — renders <title>, meta, canonical, Open Graph, Twitter, and hreflang.
 *
 * Uses React 19's native document metadata hoisting: any <title>, <meta>,
 * <link>, or <script type="application/ld+json"> rendered from a component
 * is automatically lifted into <head>. Older SPA helpers (react-helmet)
 * are unnecessary.
 */

import {
  OG_IMAGE_DEFAULT,
  SITE_LOCALE,
  SITE_NAME,
  SITE_TITLE_DEFAULT,
  SITE_DESCRIPTION_DEFAULT,
  absUrl,
} from "@/lib/seo/site";
import { safeJsonLd } from "@/lib/seo/safeJson";

export const SEO = ({
  title,
  description,
  path = "/",
  ogImage,
  ogType = "website",
  keywords,
  noindex = false,
  jsonLd,
}) => {
  const finalTitle = title || SITE_TITLE_DEFAULT;
  const finalDesc = description || SITE_DESCRIPTION_DEFAULT;
  const canonical = absUrl(path);
  const image = ogImage || OG_IMAGE_DEFAULT;

  return (
    <>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}
      <link rel="canonical" href={canonical} />

      {/* Locale + Australia-ready hreflang scaffold */}
      <link rel="alternate" hrefLang="en-NZ" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={SITE_LOCALE} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1600" />
      <meta property="og:image:height" content="900" />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={image} />

      {/* Optional JSON-LD payload(s). Accepts single object or array. */}
      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj) => (
          <script
            key={obj["@id"] || obj["@type"] || JSON.stringify(obj).slice(0, 64)}
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: safeJsonLd(obj) }}
          />
        ))}
    </>
  );
};

export default SEO;
